import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { annotation } from '@cornerstonejs/tools';
import {
  selectAnnotation,
  selectSelectedAnnotationUID,
} from '@/state/slices/measurementSession';
import { triggerAllViewports } from '../../../utils/triggerAllViewports';
import {
  loadHidden,
  loadShown,
  saveHidden,
  saveShown,
} from '../utils/listStorage';
import type { ListRow } from '../utils/orderRows';

export function useAnnotationVisibility(rows: ListRow[], userId: string) {
  const dispatch = useDispatch();
  const selectedUID = useSelector(selectSelectedAnnotationUID);

  const setVisible = useCallback(
    (uid: string, visible: boolean) => {
      const hidden = loadHidden(userId);
      annotation.visibility.setAnnotationVisibility(uid, visible);
      // Cornerstone stops updating hover on hidden annotations, so a hover
      // flag left on would let the next canvas click select (and unhide) it.
      if (!visible) {
        const ann = annotation.state.getAnnotation(uid);
        if (ann) ann.highlighted = false;
      }
      if (visible) hidden.delete(uid);
      else hidden.add(uid);
      saveHidden(userId, hidden);
      // An annotation that starts hidden stays shown once the reader shows it.
      const shown = loadShown(userId);
      if (visible) shown.add(uid);
      else shown.delete(uid);
      saveShown(userId, shown);
      if (!visible && uid === selectedUID) dispatch(selectAnnotation(null));
      triggerAllViewports();
    },
    [userId, selectedUID, dispatch]
  );

  const toggle = (row: ListRow) => setVisible(row.uid, row.hidden);

  // Re-hydrated annotations come back visible; keep the reader's choice.
  useEffect(() => {
    const hidden = loadHidden(userId);
    const stale = rows.filter((r) => hidden.has(r.uid) && !r.hidden);
    if (!stale.length) return;
    stale.forEach((r) =>
      annotation.visibility.setAnnotationVisibility(r.uid, false)
    );
    triggerAllViewports();
  }, [rows, userId]);

  // Selecting a hidden annotation from the worksheet shows it again (R13).
  useEffect(() => {
    if (rows.some((r) => r.uid === selectedUID && r.hidden)) {
      setVisible(selectedUID as string, true);
    }
  }, [rows, selectedUID, setVisible]);

  return { toggle };
}
